import React, { useContext, useState, useEffect } from 'react'
import { bookingStep } from './steps'
import { useTranslation } from 'react-i18next'
import FormComponent from '../utils/form'
import { StepContext } from '../utils/stepContext'
import { size, isEmpty } from 'lodash'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { useSnackbar } from 'notistack'
import DetailPrice from '../utils/detailPrice'
import { withRouter, useHistory } from 'react-router-dom'
import Axios from 'axios'
import WrapAuthRoute from '../routes/WrapAuthRoutes'
import { Context } from '../Store'
import { UserContext, redirectToAuthWithFrom } from '../utils/UserContext'
import { getNextStep } from '../DiscoverStep/StepValidation'
import moment from 'moment'
import { BookingContext } from '../utils/BookingContext'
import { BOOKING_STATUS, RENT_RETENTION_TYPE } from '../constants'

function pickMonthlyPrice(booking, property) {
  if (booking.status === 'PENDING' && booking.tenant_proposal) {
    return booking.tenant_proposal
  }
  return booking.counteroffer_id != null && booking.counter_offer > 0
    ? booking.counter_offer
    : property.price
}

const Booking = (props) => {
  // eslint-disable-next-line no-unused-vars
  const [state, dispatch, loadBooking] = useContext(Context)
  const { user, isLoading } = useContext(UserContext)
  const { property, booking } = state

  // eslint-disable-next-line no-unused-vars
  const [steps, setSteps] = useState(bookingStep)
  const [payload, setPayload] = useState(state.booking)
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  const { step, incrementStep, setSteps: contextSetSteps, setStep } = useContext(StepContext)
  const stripe = useStripe()
  const { t, i18n } = useTranslation()
  const elements = useElements()
  const { hasCounteroffer, counterofferAccepted } = useContext(BookingContext)

  useEffect(() => {
    loadBooking({ id: props.match.params.bookingId }).then(({ booking, error }) => {
      let bookingStatePayload = {
        how_many_people: booking.no_guest,
        age: booking.tenant_age,
        gender: booking.tenant_gender,
        nationality: booking.tenant_nationality,
        profession: booking.tenant_reason,
        about_yourself: booking.tenant_comment,
        payment_type: booking.payment_type,
        tenant_proposal: booking.tenant_proposal,
        flexibility_status: undefined, // initially this value needs to be undefined
        current_step: 1
      }

      if (booking.start_date !== null) {
        bookingStatePayload.start_date = moment(booking.start_date).toDate()
      }

      if (booking.end_date !== null) {
        bookingStatePayload.end_date = moment(booking.end_date).toDate()

        if (moment(booking.end_date).diff(moment(booking.start_date), 'months', true) >= 12) {
          bookingStatePayload.over_12_months = RENT_RETENTION_TYPE.OVER
        } else {
          bookingStatePayload.over_12_months = RENT_RETENTION_TYPE.UNDER
        }
      }

      if( booking.status === BOOKING_STATUS.PENDING) {
        bookingStatePayload.current_step = 4
      }

      dispatch({
        type: 'SET_NEW_BOOKING_STATE',
        payload: bookingStatePayload
      })

      if (booking && !error) {
        contextSetSteps(steps)
      } else if (error === 401) {
        redirectToAuthWithFrom()
      }
    })
  }, [])

  useEffect(() => {
    setPayload({
      ...payload,
      ...state.booking,
    })
    if (!isEmpty(state.booking)) {
      const checkStepAndGo = async () => {
        const nextStep = await getNextStep(booking)
        if (nextStep < step) {
          history.replace(`/bookings/${state.booking.id}/${nextStep}`)
          setStep(nextStep)
        }
      }
      checkStepAndGo()
    }
  }, [state.booking])
  
  const handleIncrementStep = incrementStep
  const onSubmit = async (data) => {
    const _newPayload = {
      ...payload,
      ...data,
      step: step,
      property_id: property.id,
      status: step + 1 !== size(steps) ? 'DRAFT' : undefined,
      id: booking.id,
    }
    let newPayload = {
      ..._newPayload,
    }
    if (step + 1 !== size(steps)) {
      const { start_date, end_date } = _newPayload
      let datesFormatted = {
        start_date: moment(start_date).format('YYYY-MM-DD'),
        end_date: end_date === null ? moment(start_date).format('YYYY-MM-DD') : moment(end_date).format('YYYY-MM-DD'),
      }

      Object.assign(_newPayload, datesFormatted)

      let res = {}
      try {
        res = await Axios.post(`${process.env.REACT_APP_BOOKING_API}`, _newPayload)
          .then((response) => response.data)
          .catch((error) => {
            enqueueSnackbar(t(error.response.data.message), { variant: 'error' })
            throw error
          })
      } catch (e) {
        return
      }

      newPayload = {
        ...newPayload,
        ...res,
      }
    }
    setPayload(newPayload)
    let canContinue = true
    if (steps[step].handleOnSubmit) {
      const result = await steps[step].handleOnSubmit(newPayload, {
        stripe,
        elements,
        t,
        i18n,
        property,
        hasCounteroffer,
        counterofferAccepted,
        booking,
      })
      if (result && result.error) {
        canContinue = false
        enqueueSnackbar(t(result.message), { variant: 'error' })
      } else if (result && result.message) {
        enqueueSnackbar(t(result.message), { variant: 'success' })
      }
      if (result && result.data) {
        setPayload({
          ...newPayload,
          ...result.data,
        })
      }
    }
    if (step + 1 !== size(steps)) {
      if (canContinue) {
        handleIncrementStep()
      }
    }
  }

  if (!isEmpty(state.property) && !isEmpty(state.booking) && !isLoading) {
    return (
      <WrapAuthRoute>
        <div className="flex flex-col h-full lg:items-evenly">
          <FormComponent
            pageType="booking"
            steps={steps}
            defaultValues={{
              ...state.booking,
              tenant_phone_no: user.phone_no,
              tenant_proposal: pickMonthlyPrice(booking, property),
              payment_type: booking.payment_type ?? 'card',
              flexibility_status: booking?.flexibility_status ?? null,
            }}
            step={step}
            incrementStep={handleIncrementStep}
            onSubmit={onSubmit}
            payload={payload}
            detailComponent={<DetailPrice payload={payload} />}
          />
        </div>
      </WrapAuthRoute>
    )
  } else {
    return <div>Getting data...</div>
  }
}

export default withRouter(Booking)
